import { templateMacros } from '../../../common/js/url-utils';
import { RootStore } from '../../../common/js/stores/root.store';
import {
  deviceSupportsAutoPlay,
  deviceSupportsAutoPlayWithRetry
} from './utils';
import { isFeatureEnable } from '../../../common/js/features-enabling';
import {
  isAndroid,
  isIos,
  isMobileWeb,
  isWeb,
  versionIsAboveOrEqual
} from '../../../common/js/environment-utils';
import { logError, logMessage } from '../../../common/js/sentry';
import {
  OMID_VAST_URL_MACROS,
  OMID_VPAID_VAST_URL_MACROS,
  selectMediaFiles,
  VPAID_VAST_URL_MACROS
} from './video';
import VASTClient from './vast/vast-client';
import VASTError, {
  CLIENT_FETCH_ERROR_PREFIX,
  EMPTY_VAST_ERROR_PREFIX,
  SERVER_FETCH_ERROR_PREFIX
} from './vast/vast-error';
import {
  getNonVpaidMediaFiles,
  getTrackingFramework,
  isMediaFileVpaid,
  TRACKING_FRAMEWORK_VPAID
} from '../../../common/js/tracking-helper';
import {
  checkVideoContentLength,
  createLinkPreloadTag,
  prefetchImages,
  prefetchVideoBlobUrl,
  prefetchVideoBlobUrlAsync,
  prefetchVideoURL,
  prefetchVideoURLAsync
} from './video-prefetch';
import {
  sendCreativeVideoErrorPrefetch,
  sendCreativeVideoErrorVastDownload,
  sendMonitoringVideoVastTimeoutEvent
} from '../../../common/js/TrackAnalyticsHelper';
import { selectBestMediaFiles } from './select-best-video';
import {
  excludeVideoTooLarge,
  filterByTargetRatio,
  getOrientationFromSize,
  groupByMimeType,
  isExcludedType,
  normalizeTarget,
  pickByWidth,
  sortBySdkPreferences,
  sortBySize
} from '../../../common/js/media-file-utils';
import ajaxPromises from '../../../common/js/AjaxPromises';
import { logDiagnostic } from './diagnostic';
import {
  FetchErrorType,
  FetchError,
  NetworkFetchError,
  TimeoutFetchError
} from '@ogury/vast-library/common/utils/fetch-error';
import { isStandardBannersAdUnit } from '../../../common/js/adUnit';
import { MediaFile } from './vast/media-file';
import { AdContainerSize } from '../../../common/domain/ad-container.domain';

const VAST_FETCH_DOWNLOAD_TIMEOUT = 3000;
const VIDEO_PREFETCH_DOWNLOAD_TIMEOUT = 15000;
const minAsyncAndroidSdkVersion = '4.3.0';
const minAsyncIOSSdkVersion = '3.2.0';

export function canUseAsyncLoading(context: AdContext): boolean {
  if (isWeb() || isStandardBannersAdUnit(context.adUnitType || '')) {
    return true;
  }

  if (!context.sdkVersion) {
    return true;
  }

  return (
    (isAndroid() &&
      versionIsAboveOrEqual(context.sdkVersion, minAsyncAndroidSdkVersion)) ||
    (isIos() &&
      versionIsAboveOrEqual(context.sdkVersion, minAsyncIOSSdkVersion))
  );
}

interface CachedVideoResult {
  cachedVideoURL: string | null;
  blob: Blob | null;
}

function getVideoUrlFromCachedVideoURL(
  cachedVideoURL: string,
  enableBase64Download: boolean = false,
  forceDisableBase64Download: boolean = false,
  context: AdContext
): CachedVideoResult | Promise<CachedVideoResult> {
  const useAsync = canUseAsyncLoading(context);

  if (!cachedVideoURL) {
    return useAsync
      ? Promise.resolve({ cachedVideoURL: null, blob: null })
      : { cachedVideoURL: null, blob: null };
  }

  const isOffline = location.protocol === 'file:';
  if (isOffline) {
    return useAsync
      ? Promise.resolve({ cachedVideoURL, blob: null })
      : { cachedVideoURL, blob: null };
  }

  if (!isFeatureEnable('webviewFormatOguryPrecaching')) {
    return useAsync
      ? Promise.resolve({ cachedVideoURL, blob: null })
      : { cachedVideoURL, blob: null };
  }

  let blob: Blob | null = null;

  const onPrefetchBlobVideoError = function(error: Error): CachedVideoResult {
    if (Math.random() > 0.8) {
      logMessage('prefetch video as blob url error', {
        message: error.message
      });
    }
    sendCreativeVideoErrorPrefetch();

    return { cachedVideoURL, blob };
  };

  const onPrefetchVideoUrlError = function(error: Error): CachedVideoResult {
    logMessage('prefetch video error', {
      message: error.message
    });
    sendCreativeVideoErrorPrefetch();
    return { cachedVideoURL, blob };
  };

  const isBase64PrefetchEnable = isIos() || enableBase64Download;
  if (isBase64PrefetchEnable && !forceDisableBase64Download) {
    try {
      const response = useAsync
        ? prefetchVideoBlobUrlAsync(
            cachedVideoURL,
            VIDEO_PREFETCH_DOWNLOAD_TIMEOUT
          )
        : prefetchVideoBlobUrl(cachedVideoURL);
      return ajaxPromises.waitPromiseOrExecute(
        response,
        (resp: { url: string; blob: Blob | null }): CachedVideoResult => {
          cachedVideoURL = resp.url;
          blob = resp.blob;
          return { cachedVideoURL, blob };
        },
        onPrefetchBlobVideoError
      ) as CachedVideoResult | Promise<CachedVideoResult>;
    } catch (error) {
      return onPrefetchBlobVideoError(error as Error);
    }
  } else {
    try {
      const response = useAsync
        ? prefetchVideoURLAsync(cachedVideoURL, {
            timeout: VIDEO_PREFETCH_DOWNLOAD_TIMEOUT
          })
        : prefetchVideoURL(cachedVideoURL);

      return ajaxPromises.waitPromiseOrExecute(
        response,
        (): CachedVideoResult => {
          return { cachedVideoURL, blob };
        },
        onPrefetchVideoUrlError
      ) as CachedVideoResult | Promise<CachedVideoResult>;
    } catch (error) {
      return onPrefetchVideoUrlError(error as Error);
    }
  }
}

function logVastContent(
  debugVastContent: boolean | undefined,
  vastContent: string | undefined,
  vastURL: string
): void {
  if (debugVastContent) {
    const shouldLogVastContent = Math.floor(Math.random() * 25) === 1;

    if (shouldLogVastContent) {
      if (vastContent) {
        logMessage('Debug VAST content', undefined, vastContent);
      } else if (vastURL) {
        fetch(vastURL)
          .then(response => response.text())
          .then(data => {
            logMessage('Debug VAST content', undefined, data);
          })
          .catch(() => {
            logMessage('Unable to debug VAST content vastURL' + vastURL);
          });
      }
    }
  }
}

function customizeVastErrorMessage(
  newVastError: VASTError,
  error: any
): string {
  // https://github.com/Ogury/vast-library/blob/240b138bd418b85510261d9bf10b9779a58c0a7b/common/utils/fetch.ts#L25
  if (
    error.type === FetchErrorType.ERROR &&
    error.message === 'URL is required' &&
    error.statusCode === 400
  ) {
    return `${EMPTY_VAST_ERROR_PREFIX}_${error.statusCode}`;
  } else if (
    error.type === FetchErrorType.ERROR &&
    (error.statusCode === 400 || error.statusCode === 422)
  ) {
    // Handle empty vast errors with status code 400 or 422
    return `${EMPTY_VAST_ERROR_PREFIX}_${error.statusCode}`;
  } else if (
    error.type === FetchErrorType.TIMEOUT &&
    error.statusCode === 408
  ) {
    return `${CLIENT_FETCH_ERROR_PREFIX}_${error.statusCode}`;
  } else if (error.statusCode === 0) {
    return `${CLIENT_FETCH_ERROR_PREFIX}_${error.statusCode}`;
  }

  return newVastError.e_fetch_error || '';
}

interface LoadVideoResult {
  vastClient: VASTClient;
  mediaFiles: MediaFile[];
  config?: any;
}

export function loadVideo(
  config: FormatConfig,
  macros: Record<string, any>,
  context: AdContext
): LoadVideoResult | Promise<LoadVideoResult> {
  const {
    soundOnStart,
    billedAfter,
    vastContent,
    vast,
    cachedVideo,
    chooseBestVideo,
    debugVastContent,
    forceVideoQuality,
    enableLinkPreload,
    enableBase64Download,
    forceDisableBase64Download,
    maxFileSize,
    newVideoSelection
  } = config.video || {};

  const { isProgrammatic } = config;
  const { skinTop, skinBottom } = config.skin || {};

  const vastUrlMacros = getVastUrlMacros(config, context);

  const options = {
    muted: !soundOnStart,
    duration: billedAfter,
    macros,
    vastUrlMacros
  };

  const vastURL = templateMacros(vast || '', macros);
  let rVastClient: VASTClient | null = null;

  const onVastClientError = (error: any) => {
    if (error && error.type === 'TIMEOUT') {
      sendMonitoringVideoVastTimeoutEvent();
    } else if (
      error &&
      typeof error === 'string' &&
      error.indexOf('fetch timeout') > -1
    ) {
      sendMonitoringVideoVastTimeoutEvent();
    }

    logDiagnostic(
      {
        origin: 'VideoLoader',
        event: 'VastDownloadError',
        fetchErrorType: error.type,
        fetchErrorName: error.name,
        fetchErrorUrl: error.url,
        fetchErrorStatusCode: error.statusCode,
        errorCode: error.code,
        errorMsg: error.message,
        errorObject: error,
        fetchedRawVasts: rVastClient ? rVastClient.getFetchedRawVasts() : [],
        shouldBeVastFetchError: error instanceof FetchError,
        shouldBeVastNetworkFetchError: error instanceof NetworkFetchError,
        shouldBeVastTimeoutFetchError: error instanceof TimeoutFetchError,
        userAgent: navigator.userAgent || 'undefined',
        ...(context && { canUseAsyncLoading: canUseAsyncLoading(context) })
      },
      { context }
    );

    if (
      [
        FetchErrorType.ERROR,
        FetchErrorType.NETWORK_ERROR,
        FetchErrorType.TIMEOUT
      ].indexOf(error.type) > -1
    ) {
      logError(error);

      const newVastError = new VASTError(
        error.statusCode,
        error.message,
        `${SERVER_FETCH_ERROR_PREFIX}_${error.statusCode}`
      );

      newVastError.e_fetch_error = customizeVastErrorMessage(
        newVastError,
        error
      );
      sendCreativeVideoErrorVastDownload(newVastError);
    } else if (
      error instanceof Error &&
      error.message &&
      error.message.indexOf('Invalid VAST document') > -1
    ) {
      // Handle invalid VAST content (not empty, but invalid XML)
      let newVastError = new VASTError(
        500,
        error.message,
        `${EMPTY_VAST_ERROR_PREFIX}_500`
      );
      sendCreativeVideoErrorVastDownload(newVastError);
    } else if (typeof error === 'string' && error.indexOf('Ad blocked') > -1) {
      const newVastError = new VASTError(
        901,
        error,
        `${SERVER_FETCH_ERROR_PREFIX}_901`
      );
      sendCreativeVideoErrorVastDownload(newVastError);
    } else {
      sendCreativeVideoErrorVastDownload(error);
    }
    throw error;
  };

  const onAfterVastClientGeneration = function(
    vastClient: VASTClient
  ): LoadVideoResult | Promise<LoadVideoResult> {
    logVastContent(debugVastContent, vastContent, vastURL);
    rVastClient = vastClient;

    const forceVastVideoQuality =
      forceVideoQuality &&
      (config.video as any)[`vastVideo${forceVideoQuality}`];

    const maxSize = isWeb() ? 3500 : maxFileSize;

    const mediaFiles = getMediaFiles(
      forceVastVideoQuality,
      cachedVideo,
      chooseBestVideo,
      vastClient,
      context,
      newVideoSelection,
      maxSize
    );

    let blob: Blob | null = null;
    const trackingFramework = getTrackingFramework(mediaFiles, config);

    const onAfterPrecaching = function(): LoadVideoResult {
      mediaFiles[0].duration = vastClient.getDuration();

      checkVideoContentLength(mediaFiles[0], maxSize || 0, blob);

      prefetchImages([skinTop, skinBottom]);

      return {
        vastClient,
        mediaFiles,
        config
      };
    };

    if (trackingFramework !== TRACKING_FRAMEWORK_VPAID) {
      const nonVpaidMediaFile = getNonVpaidMediaFiles(mediaFiles);
      if (nonVpaidMediaFile.length > 0 && isMediaFileVpaid(mediaFiles[0])) {
        mediaFiles[0] = nonVpaidMediaFile[0];
      }

      if (enableLinkPreload || isMediaFileVpaid(mediaFiles[0])) {
        createLinkPreloadTag(mediaFiles[0].url);
      } else {
        const response = getVideoUrlFromCachedVideoURL(
          mediaFiles[0].url,
          enableBase64Download,
          forceDisableBase64Download,
          context
        );

        return ajaxPromises.waitPromiseOrExecute(
          response,
          (resp: CachedVideoResult) => {
            blob = resp.blob;
            mediaFiles[0].url = resp.cachedVideoURL || mediaFiles[0].url;

            return onAfterPrecaching();
          }
        ) as LoadVideoResult | Promise<LoadVideoResult>;
      }
    }

    return onAfterPrecaching();
  };

  try {
    const useAsync = canUseAsyncLoading(context);
    const clientOptions = {
      ...options,
      ...(useAsync && {
        timeout: VAST_FETCH_DOWNLOAD_TIMEOUT,
        timeoutStrategy: 'exponential'
      }),
      // VAST options from config override defaults if set
      ...(config.video && config.video.vastOptions)
    };

    const vastClientResult = useAsync
      ? generateVastClientAsync(
          vastContent,
          vastURL,
          clientOptions,
          isProgrammatic
        )
      : generateVastClient(vastContent, vastURL, clientOptions, isProgrammatic);

    return ajaxPromises.waitPromiseOrExecute(
      vastClientResult,
      onAfterVastClientGeneration,
      (error: any) => {
        return onVastClientError(error);
      }
    ) as LoadVideoResult | Promise<LoadVideoResult>;
  } catch (error) {
    onVastClientError(error);
    throw error;
  }
}

interface VastUrlMacro {
  key: string;
  value: string | number;
}

export function getVastUrlMacros(
  config: FormatConfig,
  context: Partial<AdContext> = {}
): VastUrlMacro[] {
  const macrosByConfigurationMap: Record<string, VastUrlMacro[]> = {
    'vpaid,omid': OMID_VPAID_VAST_URL_MACROS,
    'vpaid,no_omid': VPAID_VAST_URL_MACROS,
    'no_vpaid,omid': OMID_VAST_URL_MACROS,
    'no_vpaid,no_omid': []
  };

  const mapKey: string = [
    config.hasVpaidTracking ? 'vpaid' : 'no_vpaid',
    config.omidTrackingEnabled ? 'omid' : 'no_omid'
  ].join(',');

  if (Object.keys(context).length === 0)
    return macrosByConfigurationMap[mapKey];

  let advertEnvironmentType: string | null = null;

  if (isMobileWeb()) {
    advertEnvironmentType = 'mWeb';
  } else if (isWeb()) {
    advertEnvironmentType = 'web';
  } else {
    advertEnvironmentType = 'InApp';
  }

  const now = Date.now();

  return [
    ...macrosByConfigurationMap[mapKey],
    { key: 'ad_unit_type', value: context.adUnitType || '' },
    { key: 'campaignid', value: context.campaignId || '' },
    { key: 'creative_id', value: context.creativeId || '' },
    { key: 'app_bundle', value: context.appBundle || '' },
    { key: 'advert_environment_type', value: advertEnvironmentType },
    { key: 'advertiser_name', value: context.advertiserName || '' },
    { key: 'advertiser_id', value: context.advertiserId || '' },
    { key: 'RANDOM', value: now },
    { key: 'CACHEBUSTING', value: now },
    { key: 'TIMESTAMP', value: now }
  ];
}

export function generateVastClient(
  vastContent: string | undefined,
  vastURL: string,
  options: {
    muted: boolean;
    duration?: number;
    macros: Record<string, any>;
    vastUrlMacros: VastUrlMacro[];
  },
  isProgrammatic: boolean = false
): VASTClient {
  if (vastContent) {
    return VASTClient.createClientFromContent(vastContent, options as any);
  }

  if (isProgrammatic) {
    throw new VASTError(
      400,
      'No VAST content for programmatic ad',
      `${EMPTY_VAST_ERROR_PREFIX}_400`
    );
  }

  try {
    return VASTClient.createClientFromURL(vastURL, options as any);
  } catch (error) {
    throw error;
  }
}

export function generateVastClientAsync(
  vastContent: string | undefined,
  vastURL: string,
  options: {
    muted: boolean;
    duration?: number;
    macros: Record<string, any>;
    vastUrlMacros: VastUrlMacro[];
    timeout?: number;
    timeoutStrategy?: 'exponential' | 'fixed';
    retries?: number;
    retryDelay?: number;
    maxTotalTime?: number;
  },
  isProgrammatic: boolean = false
): Promise<VASTClient> {
  try {
    if (vastContent) {
      return VASTClient.createClientFromContentAsync(
        vastContent,
        options as any
      );
    }

    if (isProgrammatic) {
      throw new VASTError(
        400,
        'No VAST content for programmatic ad',
        `${EMPTY_VAST_ERROR_PREFIX}_400`
      );
    }

    return VASTClient.createClientFromURLAsync(vastURL, options as any);
  } catch (error) {
    throw error;
  }
}

export function getMediaFiles(
  forceVastVideoQuality: string | undefined,
  cachedVideo: string | undefined,
  chooseBestVideo: boolean | undefined,
  vastClient: VASTClient,
  context: AdContext,
  newVideoSelection: boolean | undefined,
  maxSize: number | undefined
): MediaFile[] {
  const forceVideoUrl = forceVastVideoQuality || cachedVideo;

  if (forceVideoUrl) {
    return [
      {
        url: forceVideoUrl,
        type: 'video/mp4',
        width: 0,
        height: 0,
        bitrate: 0,
        duration: 0
      }
    ];
  } else {
    let mediaFiles: MediaFile[] = [];

    if (newVideoSelection) {
      let targetSize: AdContainerSize | false =
        isWeb() &&
        RootStore.getInstance().adContainerStore.adContainerPort.getMaxSize();

      if (
        targetSize &&
        (targetSize as any).width === 0 &&
        (targetSize as any).height === 0
      ) {
        targetSize = false;
      }

      const rawTarget = targetSize || screen || { width: 1, height: 1 };
      const norm = normalizeTarget(rawTarget as any);

      mediaFiles = selectMediaFilesWithNewAlgorithm(
        vastClient.getMediaFiles(),
        maxSize || 0,
        { width: norm.width, height: norm.height }
      );
    } else if (chooseBestVideo) {
      mediaFiles = selectBestMediaFiles(vastClient.getMediaFiles(), context);
    } else {
      mediaFiles = selectMediaFiles(vastClient.getMediaFiles());
    }

    if (!mediaFiles || mediaFiles.length < 1 || !mediaFiles[0].url) {
      throw new VASTError(
        403,
        'Could not find a media file supported by the video player',
        `${EMPTY_VAST_ERROR_PREFIX}_403`
      );
    } else {
      return mediaFiles;
    }
  }
}

export function deviceSupportAutoplayDetect(
  alternativeAutoplayDetect: boolean,
  alternativeAutoplayLog: string
): Promise<void> {
  let errorMsg: string;
  let checkPromise: Promise<boolean>;

  if (alternativeAutoplayDetect) {
    errorMsg = 'The video player does not support autoplay v2';
    checkPromise = deviceSupportsAutoPlayWithRetry({
      logErrorsToSentry: !!alternativeAutoplayLog,
      retries: 5,
      timeout: 200
    });
  } else {
    errorMsg = 'The video player does not support autoplay';
    checkPromise = deviceSupportsAutoPlay();
  }

  return checkPromise.then(canAutoPlay => {
    if (!canAutoPlay) {
      throw new VASTError(400, errorMsg);
    }
  });
}

export function selectMediaFilesWithNewAlgorithm(
  mediaFiles: MediaFile[],
  limitSize: number,
  targetSize: { width: number; height: number }
): MediaFile[] {
  const orientation = getOrientationFromSize(targetSize);

  const mediaFilesWithoutExcludedType = mediaFiles
    .filter(mediaFile => !isExcludedType(mediaFile as any))
    .map(mediaFile => {
      if (mediaFile.bitrate <= 0) return mediaFile;
      const sizeDelta =
        (mediaFile.width - targetSize.width) / Math.max(1, targetSize.width);
      const penalty =
        sizeDelta > 0
          ? Math.round(sizeDelta * 1000)
          : Math.round(sizeDelta * 300);
      return {
        ...mediaFile,
        bitrate: Number(mediaFile.bitrate),
        additionalBitrate: penalty
      };
    });

  const bestMediaFileByMimeType = groupByMimeType(
    mediaFilesWithoutExcludedType as any[]
  ).map((mediaFilesByMimeType: any[]) => {
    let allMediaFilesTooLarge = false;

    let mediaFilesNotTooLarge = excludeVideoTooLarge(
      mediaFilesByMimeType,
      limitSize
    );

    if (mediaFilesNotTooLarge.length === 0) {
      mediaFilesNotTooLarge = mediaFilesByMimeType;
      allMediaFilesTooLarge = true;
    }

    const mediaFilesFilterByRatio = filterByTargetRatio(
      mediaFilesNotTooLarge,
      targetSize.width / targetSize.height
    );
    const mediaFilesSortedBySize = sortBySize(mediaFilesFilterByRatio);

    const best = pickByWidth(
      mediaFilesSortedBySize,
      targetSize.width,
      orientation
    );

    if (allMediaFilesTooLarge && mediaFilesSortedBySize.length > 0) {
      return mediaFilesSortedBySize[0];
    }

    return best;
  });

  return sortBySdkPreferences(bestMediaFileByMimeType) as MediaFile[];
}
